import { useEffect, useId, useState } from 'react'
import type { PinVault } from '@application/ports.ts'
import { isLocked, remainingLockMs } from '@domain/rules/pinLockout.ts'
import { es } from '../strings/es.ts'
import { PinField } from '../shell/PinField.tsx'

/**
 * The door to parent mode. The lockout comes from the domain rule; this only
 * reports what the rule decided, and the keypad layout never moves.
 */
export function PinGate({
  vault,
  onOpen,
  onCancel,
}: {
  vault: PinVault
  onOpen: () => void
  onCancel: () => void
}) {
  const [pin, setPin] = useState('')
  const [confirm, setConfirm] = useState('')
  const [isSet, setIsSet] = useState<boolean | null>(null)
  const [unreadable, setUnreadable] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const pinId = useId()
  const confirmId = useId()

  useEffect(() => {
    let cancelled = false
    void vault
      .state()
      .then((state) => {
        if (!cancelled) setIsSet(state.isSet)
      })
      .catch(() => {
        // Esta puerta falla CERRADA, al contrario que la de los niños. Si no se
        // puede saber si hay PIN, no se ofrece el formulario: enseñarlo con
        // `isSet` en falso dejaría poner uno nuevo y entrar, que es exactamente
        // lo que el PIN evita.
        if (!cancelled) setUnreadable(true)
      })
    return () => {
      cancelled = true
    }
  }, [vault])

  if (unreadable) {
    return (
      <div className="mx-auto max-w-sm px-5 pt-16" role="alert">
        <p className="text-coral font-bold">{es.shell.loadFailed}</p>
        <p className="text-muted text-sm">{es.shell.loadFailedHint}</p>
        <button type="button" onClick={onCancel} className="text-muted mt-4 text-sm font-bold">
          {es.today.back}
        </button>
      </div>
    )
  }

  if (isSet === null) {
    return <p className="text-muted p-8">{es.shell.checking}</p>
  }

  const submit = async (): Promise<void> => {
    setBusy(true)
    try {
      if (!isSet) {
        if (!/^\d{4,8}$/.test(pin)) {
          setError(/\D/.test(pin) ? es.pin.errorDigits : es.pin.errorShort)
          return
        }
        if (pin !== confirm) {
          setError(es.pin.errorMatch)
          return
        }
        await vault.set(pin)
        onOpen()
        return
      }

      const state = await vault.state()
      const now = new Date()
      if (state.lastFailureAt && isLocked(state.consecutiveFailures, state.lastFailureAt, now)) {
        const seconds = Math.ceil(
          remainingLockMs(state.consecutiveFailures, state.lastFailureAt, now) / 1000,
        )
        setError(es.pin.lockedFor(seconds))
        return
      }
      if (await vault.verify(pin)) {
        onOpen()
      } else {
        setError(es.pin.errorWrong)
        setPin('')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-5">
      <h1 className="mb-2 text-3xl font-extrabold tracking-tight">{es.pin.title}</h1>
      <p className="text-muted mt-0 mb-6 text-sm">{es.pin.explain}</p>

      <PinField
        id={pinId}
        label={isSet ? es.pin.enter : es.pin.createField}
        value={pin}
        onChange={setPin}
      />

      {!isSet ? (
        <PinField id={confirmId} label={es.pin.repeat} value={confirm} onChange={setConfirm} />
      ) : null}

      {error ? (
        <p role="alert" className="text-coral mt-0 mb-4 text-sm font-bold">
          {error}
        </p>
      ) : null}

      <button
        type="button"
        disabled={busy}
        className="bg-teal mb-2 w-full rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-50"
        onClick={() => {
          void submit()
        }}
      >
        {isSet ? es.pin.submit : es.pin.save}
      </button>
      <button type="button" className="text-muted text-sm font-bold" onClick={onCancel}>
        {es.pin.cancel}
      </button>
    </div>
  )
}
